/**
 * TeamPerformanceDashboard Component
 * Main dashboard for managers to view team performance
 */

import React, { useState, useMemo } from 'react';
import { Users, TrendingUp, TrendingDown, Minus, Target, CheckCircle } from 'lucide-react';
import { Card } from '../../common';
import { PDFExportButton } from '../PDFExportButton';
import { DirectReportCard } from './DirectReportCard';
import { TeamPerformanceTable } from './TeamPerformanceTable';
import { DirectReportModal } from './DirectReportModal';
import { useTeamPerformance, useDirectReportPerformance, usePerformancePermissions } from '../../../hooks';
import { useAuth } from '../../../context/AuthContext';
import type { TeamMemberPerformance, DirectReportPerformance, PerformanceQueryParams } from '../../../types';
import { getScoreColor } from '../../../types';

export interface TeamPerformanceDashboardProps {
  /** Period chosen by the page's master control. */
  queryParams: PerformanceQueryParams;
  /**
   * Person whose detail panel is open. Owned by the page (it is kept in the URL) so the
   * panel is still open after the user opens one of their OKRs and presses Back.
   */
  selectedUserId: string | null;
  onSelectUser: (userId: string | null) => void;
  onOKRClick?: (okrId: string) => void;
}

// Helper to convert DirectReportPerformance to TeamMemberPerformance
function toTeamMemberPerformance(report: DirectReportPerformance): TeamMemberPerformance {
  return {
    userId: report.userId,
    userName: report.userName,
    userEmail: report.userEmail,
    score: report.score,
    previousScore: undefined, // Not available in DirectReportPerformance
    okrProgress: 0, // Would need to fetch separately
    checkInCompliance: 0,
    engagement: 0,
    okrCount: report.okrCount,
    okrsCompleted: 0, // Not available directly
  };
}

export const TeamPerformanceDashboard: React.FC<TeamPerformanceDashboardProps> = ({
  queryParams,
  selectedUserId,
  onSelectUser,
  onOKRClick,
}) => {
  const { user } = useAuth();
  const permissions = usePerformancePermissions();
  const periodType = queryParams.periodType ?? 'quarterly';
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  // Fetch team performance
  const { data: teamData, isLoading: isTeamLoading } = useTeamPerformance(queryParams);

  // Fetch selected direct report's detailed performance
  const { data: selectedReportData, isLoading: isReportLoading } = useDirectReportPerformance(
    selectedUserId || '',
    queryParams,
    !!selectedUserId
  );

  const handleMemberClick = (userId: string) => onSelectUser(userId);

  const handleCloseModal = () => onSelectUser(null);

  // Convert direct reports to team member performance format
  const teamMembers: TeamMemberPerformance[] = useMemo(() => {
    if (!teamData?.directReports) return [];
    return teamData.directReports.map(toTeamMemberPerformance);
  }, [teamData]);

  // Calculate team stats
  const teamStats = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) {
      return {
        averageScore: teamData?.averageScore || 0,
        highPerformers: 0,
        needsAttention: 0,
        totalOKRs: 0,
        completedOKRs: 0,
        trend: 0,
      };
    }

    const highPerformers = teamMembers.filter((m) => m.score >= 80).length;
    const needsAttention = teamMembers.filter((m) => m.score < 50).length;
    const totalOKRs = teamMembers.reduce((sum, m) => sum + m.okrCount, 0);
    const completedOKRs = teamMembers.reduce((sum, m) => sum + m.okrsCompleted, 0);

    // Calculate average trend if previous scores available
    const membersWithTrend = teamMembers.filter((m) => m.previousScore !== undefined);
    const trend =
      membersWithTrend.length > 0
        ? membersWithTrend.reduce((sum, m) => sum + (m.score - (m.previousScore || 0)), 0) /
          membersWithTrend.length
        : 0;

    return {
      averageScore: teamData?.averageScore || 0,
      highPerformers,
      needsAttention,
      totalOKRs,
      completedOKRs,
      trend,
    };
  }, [teamMembers, teamData]);

  const selectedMember = useMemo(() => {
    if (!selectedUserId || !teamMembers) return null;
    return teamMembers.find((m) => m.userId === selectedUserId);
  }, [selectedUserId, teamMembers]);

  return (
    <div className="space-y-6">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Team Performance</h2>
          <p className="text-sm text-stone-500">
            View and track your team's performance metrics
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex items-center border border-stone-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'cards'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Table
            </button>
          </div>
          {permissions.canExport && (
            <PDFExportButton
              type="team"
              data={teamData || null}
              periodType={periodType}
              tenantName={user?.organizationName}
              disabled={isTeamLoading || !teamData}
              variant="secondary"
              size="md"
            />
          )}
        </div>
      </div>

      {/* Team Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Team Average Score */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Team Average</p>
              <div className="flex items-center gap-2">
                <span
                  className="text-2xl font-bold"
                  style={{ color: getScoreColor(teamStats.averageScore) }}
                >
                  {isTeamLoading ? '-' : Math.round(teamStats.averageScore)}
                </span>
                {!isTeamLoading && teamStats.trend !== 0 && (
                  <span
                    className={`flex items-center text-sm ${
                      teamStats.trend > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {teamStats.trend > 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    {teamStats.trend > 0 ? '+' : ''}{teamStats.trend.toFixed(1)}
                  </span>
                )}
                {!isTeamLoading && teamStats.trend === 0 && (
                  <Minus className="w-4 h-4 text-stone-400" />
                )}
              </div>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
        </Card>

        {/* High Performers */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">High Performers</p>
              <span className="text-2xl font-bold text-green-600">
                {isTeamLoading ? '-' : teamStats.highPerformers}
              </span>
              <span className="text-sm text-stone-400 ml-1">
                / {teamData?.teamSize || 0}
              </span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
          </div>
        </Card>

        {/* Needs Attention */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">Needs Attention</p>
              <span
                className={`text-2xl font-bold ${
                  teamStats.needsAttention > 0 ? 'text-amber-600' : 'text-stone-400'
                }`}
              >
                {isTeamLoading ? '-' : teamStats.needsAttention}
              </span>
              <span className="text-sm text-stone-400 ml-1">members</span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
              <Target className="w-6 h-6 text-amber-500" />
            </div>
          </div>
        </Card>

        {/* OKR Completion */}
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-500">OKR Completion</p>
              <span className="text-2xl font-bold text-stone-800">
                {isTeamLoading ? '-' : teamStats.completedOKRs}
              </span>
              <span className="text-sm text-stone-400 ml-1">
                / {teamStats.totalOKRs} OKRs
              </span>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-purple-500" />
            </div>
          </div>
        </Card>
      </div>

      {/* Team Members */}
      {viewMode === 'cards' ? (
        <div>
          <h3 className="text-lg font-semibold text-stone-800 mb-4">Direct Reports</h3>
          {isTeamLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <DirectReportCard
                  key={i}
                  userId=""
                  userName=""
                  score={0}
                  okrCount={0}
                  okrsCompleted={0}
                  isLoading={true}
                />
              ))}
            </div>
          ) : !teamMembers || teamMembers.length === 0 ? (
            <Card className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-2 text-stone-300" />
              <p className="text-stone-500">No direct reports found</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teamMembers.map((member) => (
                <DirectReportCard
                  key={member.userId}
                  userId={member.userId}
                  userName={member.userName}
                  userEmail={member.userEmail}
                  avatarUrl={member.avatarUrl}
                  score={member.score}
                  previousScore={member.previousScore}
                  okrCount={member.okrCount}
                  okrsCompleted={member.okrsCompleted}
                  onClick={handleMemberClick}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <TeamPerformanceTable
          members={teamMembers}
          onMemberClick={handleMemberClick}
          isLoading={isTeamLoading}
        />
      )}

      {/* Direct Report Detail Modal */}
      {selectedMember && (
        <DirectReportModal
          isOpen
          onClose={handleCloseModal}
          userId={selectedMember.userId}
          userName={selectedMember.userName}
          userEmail={selectedMember.userEmail}
          avatarUrl={selectedMember.avatarUrl}
          performanceData={selectedReportData}
          previousScore={selectedMember.previousScore}
          isLoading={isReportLoading}
          onOKRClick={onOKRClick}
        />
      )}
    </div>
  );
};

export default TeamPerformanceDashboard;
